/**
 * Field ID Utilities
 *
 * Provides consistent field identification using CVR-Marknr as unique key.
 * This replaces the incorrect use of MARKBLOKNR which is NOT unique
 * (multiple marks can share the same Markblok).
 */

/**
 * Generates a unique field ID from CVR and Marknr.
 * Format: "CVR-Marknr" (e.g., "18245809-47-0")
 *
 * @param feature - GeoJSON feature with properties.CVR and properties.Marknr
 * @returns Unique field identifier string
 */
export const getFieldId = (feature: any): string => {
  const cvr = feature?.properties?.CVR || '';
  const marknr = feature?.properties?.Marknr || '';
  return `${cvr}-${marknr}`;
};

/**
 * Generates a unique field ID from individual CVR and Marknr values.
 *
 * @param cvr - CVR number
 * @param marknr - Mark number
 * @returns Unique field identifier string
 */
export const createFieldId = (cvr: string, marknr: string): string => {
  return `${cvr}-${marknr}`;
};

/**
 * Extracts CVR from a field ID.
 *
 * @param fieldId - Field ID in format "CVR-Marknr"
 * @returns CVR portion of the ID
 */
export const getCvrFromFieldId = (fieldId: string): string => {
  const dashIndex = fieldId.indexOf('-');
  if (dashIndex === -1) return fieldId;
  return fieldId.substring(0, dashIndex);
};

/**
 * Extracts Marknr from a field ID.
 *
 * @param fieldId - Field ID in format "CVR-Marknr"
 * @returns Marknr portion of the ID
 */
export const getMarknrFromFieldId = (fieldId: string): string => {
  const dashIndex = fieldId.indexOf('-');
  if (dashIndex === -1) return '';
  return fieldId.substring(dashIndex + 1);
};

/**
 * Checks if a field ID is valid (has both CVR and Marknr).
 *
 * @param fieldId - Field ID to validate
 * @returns true if valid
 */
export const isValidFieldId = (fieldId: string): boolean => {
  if (!fieldId || typeof fieldId !== 'string') return false;
  const cvr = getCvrFromFieldId(fieldId);
  const marknr = getMarknrFromFieldId(fieldId);
  return cvr.length > 0 && marknr.length > 0;
};

/**
 * Deduplicates GeoJSON features based on CVR-Marknr.
 * Keeps the first occurrence of each unique ID.
 *
 * @param features - Array of GeoJSON features
 * @returns Deduplicated array of features
 */
export const deduplicateFeatures = (features: any[]): any[] => {
  if (!features || !Array.isArray(features)) return [];

  const seen = new Map<string, any>();
  for (const feature of features) {
    const id = getFieldId(feature);
    if (isValidFieldId(id) && !seen.has(id)) {
      seen.set(id, feature);
    }
  }
  return Array.from(seen.values());
};

/**
 * Finds a feature by its field ID in an array of features.
 *
 * @param features - Array of GeoJSON features
 * @param fieldId - Field ID to find
 * @returns The matching feature or undefined
 */
export const findFeatureById = (features: any[], fieldId: string): any | undefined => {
  if (!features || !Array.isArray(features)) return undefined;
  return features.find(f => getFieldId(f) === fieldId);
};

/**
 * Checks if a feature exists in an array by its field ID.
 *
 * @param features - Array of GeoJSON features
 * @param fieldId - Field ID to check
 * @returns true if feature exists
 */
export const hasFeatureWithId = (features: any[], fieldId: string): boolean => {
  if (!features || !Array.isArray(features)) return false;
  return features.some(f => getFieldId(f) === fieldId);
};

/**
 * Gets all field IDs from an array of features.
 *
 * @param features - Array of GeoJSON features
 * @returns Array of field IDs
 */
export const getFieldIds = (features: any[]): string[] => {
  if (!features || !Array.isArray(features)) return [];
  return features.map(f => getFieldId(f)).filter(isValidFieldId);
};

/**
 * Checks if a field ID uses the old MARKBLOKNR format.
 * Old format: "NNNNNN-NN" (6 digits, dash, 2 digits)
 * New format: "CVR-Marknr" (8 digits, dash, variable)
 *
 * @param fieldId - Field ID to check
 * @returns true if old format
 */
export const isOldMarkbloknrFormat = (fieldId: string): boolean => {
  if (!fieldId || typeof fieldId !== 'string') return false;
  // Old MARKBLOKNR format: exactly "NNNNNN-NN" (6 digits, dash, 2 digits)
  return /^\d{6}-\d{2}$/.test(fieldId);
};

/**
 * Migrates localStorage data from old MARKBLOKNR format to new CVR-Marknr format.
 * Since we can't convert old IDs to new ones (we don't have the mapping),
 * this function clears old-format selections.
 *
 * @param userId - User ID for storage key
 * @returns true if migration was performed
 */
export const migrateLocalStorageFieldIds = (userId: number): boolean => {
  const key = `fieldvision_user_${userId}_selectedFields`;
  const data = localStorage.getItem(key);

  if (!data) return false;

  try {
    const ids = JSON.parse(data);
    if (!Array.isArray(ids)) {
      localStorage.removeItem(key);
      return true;
    }

    // Check if any IDs use old format
    const hasOldFormat = ids.some(id => isOldMarkbloknrFormat(id));

    if (hasOldFormat) {
      // Clear old selections - can't convert without mapping
      localStorage.removeItem(key);
      console.log('FieldVision: Migrated old field selection format (cleared)');
      return true;
    }

    return false;
  } catch (e) {
    localStorage.removeItem(key);
    return true;
  }
};

/**
 * Migrates zoom fields localStorage from old format.
 *
 * @param userId - User ID for storage key
 * @returns true if migration was performed
 */
export const migrateLocalStorageZoomFields = (userId: number): boolean => {
  const key = `fieldvision_user_${userId}_zoomFields`;
  const data = localStorage.getItem(key);

  if (!data) return false;

  try {
    const fields = JSON.parse(data);
    if (!Array.isArray(fields)) {
      localStorage.removeItem(key);
      return true;
    }

    // Check if any fields use old MARKBLOKNR as identifier
    const hasOldFormat = fields.some(f => {
      const id = f?.properties?.MARKBLOKNR;
      return id && isOldMarkbloknrFormat(id) && !f?.properties?.CVR;
    });

    if (hasOldFormat) {
      localStorage.removeItem(key);
      console.log('FieldVision: Migrated old zoom fields format (cleared)');
      return true;
    }

    return false;
  } catch (e) {
    localStorage.removeItem(key);
    return true;
  }
};
